import { useEffect, useRef, useState } from "react";
import { X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const COPY = {
  contact: {
    title: "Contact us",
    intro: "Tell us a little about your business. We'll get right back to you.",
    mode: "lead",
  },
  "roi-assessment": {
    title: "Free ROI Assessment",
    intro: "Share a few details and we'll estimate the hours and money an AI agent could save you.",
    mode: "lead",
  },
  "drop-a-line": {
    title: "Drop us a line",
    intro: "Explain your problem in about three sentences. An engineer will revert with a preliminary blueprint.",
    mode: "drop",
  },
};

const SIZES = ["1–50", "51–200", "201–500", "500+"];

export default function LeadModal({ open, type, onClose }) {
  const cfg = COPY[type] || COPY.contact;
  const dialogRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", companySize: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", company: "", companySize: "", message: "" });
      setErrors({});
      setSuccess(false);
      setSubmitting(false);
      document.body.style.overflow = "hidden";
      setTimeout(() => dialogRef.current?.querySelector("input,textarea")?.focus(), 30);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, type]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && open && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name.";
    if (!form.email.trim()) e.email = "We'll need a way to reach you.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Looks like that email isn't quite right.";
    if (cfg.mode === "drop" && (!form.message || form.message.trim().length < 20))
      e.message = "Give us about 2–3 sentences so we can be useful.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        type,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        companySize: cfg.mode === "lead" ? form.companySize || null : null,
        message: form.message.trim() || null,
      };
      await api.post("/leads", payload);
      setSuccess(true);
      toast.success("Thanks, we've got it. One of our engineers will be in touch shortly.");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Something didn't go through. Mind trying again?";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" role="dialog" aria-modal="true" data-testid="lead-modal">
      <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={dialogRef}
        className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-mist max-h-[92vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 text-steel hover:text-navy"
          data-testid="lead-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {success ? (
          <div className="p-8 sm:p-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-teal/15 text-teal grid place-items-center">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h3 className="mt-5 text-navy text-[22px] font-extrabold tracking-tightish">Thanks, we've got it.</h3>
            <p className="mt-3 text-slate text-[15px] leading-relaxed max-w-sm mx-auto">
              One of our engineers will be in touch shortly. In the meantime, feel free to keep exploring.
            </p>
            <button
              onClick={onClose}
              className="mt-7 inline-flex items-center justify-center bg-blue hover:bg-blue-deep text-white font-semibold px-6 py-3 rounded-md"
              data-testid="lead-modal-done"
            >
              Back to the site
            </button>
          </div>
        ) : (
          <div className="p-7 sm:p-9">
            <div className="text-blue text-[11px] font-bold uppercase tracking-[0.22em]">Zeblan</div>
            <h3 className="mt-2 text-navy text-[24px] font-extrabold tracking-tightish">{cfg.title}</h3>
            <p className="mt-2 text-slate text-[14px] leading-relaxed">{cfg.intro}</p>

            <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
              <Field label="Name *" error={errors.name}>
                <input
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls(!!errors.name)}
                  placeholder="Jane Doe"
                  data-testid="lead-input-name"
                />
              </Field>
              <Field label="Work email *" error={errors.email}>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls(!!errors.email)}
                  placeholder="jane@company.com"
                  data-testid="lead-input-email"
                />
              </Field>
              <Field label="Company">
                <input
                  value={form.company}
                  onChange={update("company")}
                  className={inputCls(false)}
                  placeholder="Acme Industries"
                  data-testid="lead-input-company"
                />
              </Field>

              {cfg.mode === "lead" && (
                <Field label="Company size">
                  <div className="flex flex-wrap gap-2" data-testid="lead-input-size">
                    {SIZES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, companySize: s }))}
                        className={`text-[13px] font-medium px-3.5 py-2 rounded-full border transition-colors ${
                          form.companySize === s
                            ? "bg-blue text-white border-blue"
                            : "bg-white text-slate border-mist hover:border-blue hover:text-blue"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              <Field
                label={cfg.mode === "drop" ? "Describe your problem (~3 sentences) *" : "Message"}
                error={errors.message}
                hint={cfg.mode === "drop" ? `${form.message.length}/600 characters` : null}
              >
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={cfg.mode === "drop" ? 5 : 3}
                  maxLength={600}
                  className={inputCls(!!errors.message)}
                  placeholder={cfg.mode === "drop" ? "What are you stuck on, and what does success look like?" : "Anything we should know?"}
                  data-testid="lead-input-message"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center bg-blue hover:bg-blue-deep disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-md transition-colors"
                data-testid="lead-submit"
              >
                {submitting ? "Sending…" : "Send"}
              </button>

              <p className="text-[12px] text-steel text-center pt-2">
                We treat your details with care. No spam, ever.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold text-navy mb-1.5">{label}</span>
      {children}
      <div className="flex items-center justify-between mt-1">
        {error ? <span className="text-[12px] text-red-600">{error}</span> : <span />}
        {hint ? <span className="text-[12px] text-steel">{hint}</span> : null}
      </div>
    </label>
  );
}

function inputCls(invalid) {
  return `w-full bg-cloud border ${
    invalid ? "border-red-500" : "border-mist"
  } focus:border-blue focus:bg-white rounded-lg px-3.5 py-2.5 text-[14px] text-navy outline-none transition-colors`;
}
